import React, { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Button, ProgressIndicator, ErrorBanner } from 'rhombus-react';
import { reduxForm, reset, change, FieldArray, SubmissionError } from "redux-form";

import { ADD_AUDIO_CREATIVES_TO_IMPOSIUM_PROJECT_FORM } from "widgets/forms/list";
import BasicModal from "widgets/modals/BasicModal";

import { createImposiumAudioFromProject, encodeAudioImposiumGrowls } from "reducers/creatives/VAST";
import { getImposiumCompositions } from "reducers/ImposiumCompositions";
import { loadCreatives } from 'reducers/Creatives';
import { addNotification } from "reducers/Notifications";

import { CreativeFieldArray } from "widgets/modals/imposium/AddCreativesToImposiumProjectModal";
import { formatAPIOption } from "widgets/social2/utils";

import "./AddCreativesToImposiumProjectModal.css";


export function AddAudioCreativesToImposiumProjectModal(props) {
	const dispatch = useDispatch();
	const { handleSubmit, submitting, error, invalid, selectedProject, navigationActions, onClose } = props;

	const isLoading = useSelector(state => state.imposiumProjects.isLoading);
	const isLoadingCompositions = useSelector(state => state.imposiumCompositions.isLoading);
	const compositions = useSelector(state => state.imposiumCompositions.items);

	function onBack() {
		dispatch(reset(ADD_AUDIO_CREATIVES_TO_IMPOSIUM_PROJECT_FORM));
		navigationActions?.popModal();
	}

	useEffect(() => {
		if (props.libraryId && selectedProject?.id) {
			dispatch(getImposiumCompositions(props.libraryId, selectedProject?.id, selectedProject?.name));
		}
	}, [props.libraryId, selectedProject]);

	useEffect(() => {
		if (!compositions?.length) {
			return;
		}
		const renderComposition = compositions.find(
			composition => composition.name?.toLowerCase() === "render"
		);
		if (renderComposition) {
			dispatch(change(
				ADD_AUDIO_CREATIVES_TO_IMPOSIUM_PROJECT_FORM,
				"creatives[0].composition",
				formatAPIOption(renderComposition)
			));
		}
	}, [compositions]);

	function renderContent() {
		return (
			<div>
				<div className="add-creatives-help-text">
					Provide a creative name and select the composition to be used for the audio creative.
				</div>
				<div className="imposium-title">
					<strong>Project name:</strong> <span>{selectedProject?.name}</span>
				</div>
				<form id={ADD_AUDIO_CREATIVES_TO_IMPOSIUM_PROJECT_FORM} onSubmit={handleSubmit} className="add-creatives-form">
					<ProgressIndicator key="loading" isLoading={submitting || isLoading || isLoadingCompositions} />
					{error && (
						<ErrorBanner
							title="Sorry, please fix the errors below and try again."
							messages={[error]}
						/>
					)}
					<div>
						<FieldArray component={CreativeFieldArray} props={props} name="creatives" />
					</div>
				</form>
			</div>
		);
	}

	function renderFooter() {
		return [
			<Button key="back" theme="default" onClick={onBack}>Back</Button>,
			<Button
				key="create"
				disabled={submitting || isLoading || invalid}
				type="submit"
				theme="green"
				form={ADD_AUDIO_CREATIVES_TO_IMPOSIUM_PROJECT_FORM}
			>
				Create Dynamic Audio Creative
			</Button>,
		];
	}

	const modalProps = {
		title: "Create Dynamic Audio - Imposium",
		className: "create-imposium-project-modal add-creatives-to-imposium-project-modal",
		onClose,
		renderContent,
		renderFooter,
	};

	return <BasicModal {...modalProps} />;
}

AddAudioCreativesToImposiumProjectModal.displayName = "AddAudioCreativesToImposiumProjectModal";

export default reduxForm({
	form: ADD_AUDIO_CREATIVES_TO_IMPOSIUM_PROJECT_FORM,
	onSubmit(values, dispatch, props) {
		const postData = values?.creatives?.map(value => ({
			creativeName: value.creativeName,
			compositionId: value.composition.value,
		}));

		return dispatch(createImposiumAudioFromProject(
			props.libraryId,
			props.selectedProject?.id,
			postData
		)).then((resp) => {
			if (!resp.success) {
				throw new SubmissionError({
					_error: resp.payload?.errorObj?.description || "Failed to create audio creatives",
				});
			}
			return { ...resp, postData };
		});
	},
	onSubmitSuccess(result, dispatch, props) {
		const numCreatives = result?.postData?.length || 1;
		dispatch(addNotification({
			message: encodeAudioImposiumGrowls(numCreatives),
			type: "success",
		}));
		props.onClose();
		dispatch(loadCreatives(props.libraryId));
	},
})(AddAudioCreativesToImposiumProjectModal);
